import type { FaceDetection, FramePixels } from "@/libs/cv"
import {
  Detector as BaseDetector,
  type Detections,
  type IlluminationDetection,
} from "@/libs/cv/detector/detector"
import { WebGLContextLostError } from "@/libs/gl/error"
import type { ImageSource } from "@/libs/image"
import { retryAsync, throttle } from "@/libs/lodash"

export type { Detections }

export class Detector extends BaseDetector {
  #recoveringPromise: Promise<void> | null = null

  override async getDetections(frame: ImageSource): Promise<Detections> {
    if (this.#recoveringPromise) await this.#recoveringPromise

    return retryAsync(() => super.getDetections(frame), 3, {
      shouldThrowEarly: (error) =>
        !(error instanceof AggregateError ? error.errors : [error]).some(
          (e) => e instanceof WebGLContextLostError,
        ),
      onRetry: async () => {
        this.#recoveringPromise = this.destroy().then(this._init)
        this.#recoveringPromise.finally(() => (this.#recoveringPromise = null))
      },
    })
  }

  #getPixelsThrottled = super._getPixels.bind(this)
  #getFaceDetectionsThrottled = super._getFaceDetections.bind(this)

  throttle({ detectionType, wait }: { detectionType: "illumination" | "face"; wait: number }) {
    if (detectionType === "illumination")
      this.#getPixelsThrottled = throttle(super._getPixels.bind(this), wait, {
        throttledResult: (_) => null,
      })
    else if (detectionType === "face")
      this.#getFaceDetectionsThrottled = throttle(super._getFaceDetections.bind(this), wait)
  }

  #lastIlluminationResult: IlluminationDetection = { azimuth: 0, intensity: 1, elevation: 1 }

  protected override _getPixels(imageSource: ImageSource): FramePixels | null {
    return this.#getPixelsThrottled(imageSource)
  }
  protected override _getFaceDetections(imageSource: ImageSource): Promise<FaceDetection | null> {
    return this.#getFaceDetectionsThrottled(imageSource)
  }
  protected override async _getIllumination(face: FaceDetection, pixels: FramePixels) {
    return (this.#lastIlluminationResult =
      (await super._getIllumination(face, pixels)) ?? this.#lastIlluminationResult)
  }
}
